import { useState, useEffect, useRef, useCallback } from 'react';

interface ProfanityFilterSettingsProps {
  serverUrl: string;
}

interface ProfanityFilterConfig {
  enabled: boolean;
  useDefaultList: boolean;
  customWords: string[];
  whitelistedWords: string[];
}

// Helper to get CSRF token from cookie
function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

export function ProfanityFilterSettings({ serverUrl }: ProfanityFilterSettingsProps) {
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [newWord, setNewWord] = useState('');
  const [newWhitelistedWord, setNewWhitelistedWord] = useState('');
  const [showPatternHelp, setShowPatternHelp] = useState(false);
  
  const [config, setConfig] = useState<ProfanityFilterConfig>({
    enabled: true,
    useDefaultList: true,
    customWords: [],
    whitelistedWords: [],
  });

  // Track if initial load is complete to avoid saving on mount
  const initialLoadComplete = useRef(false);

  useEffect(() => {
    // Fetch profanity filter settings
    fetch(`${serverUrl}/api/settings/profanity-filter`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data: { error?: string; profanityFilter?: { enabled?: boolean; useDefaultList?: boolean; customWords?: string[]; whitelistedWords?: string[] } }) => {
        if (data && !data.error && data.profanityFilter) {
          setConfig({
            enabled: data.profanityFilter.enabled ?? true,
            useDefaultList: data.profanityFilter.useDefaultList ?? true,
            customWords: data.profanityFilter.customWords ?? [],
            whitelistedWords: data.profanityFilter.whitelistedWords ?? [],
          });
        }
        initialLoadComplete.current = true;
      })
      .catch(() => {
        initialLoadComplete.current = true;
      });
  }, [serverUrl]);

  const saveSettings = useCallback(async (settings: ProfanityFilterConfig) => {
    setSaveStatus('saving');
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const res = await fetch(`${serverUrl}/api/settings/profanity-filter`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(settings),
      });

      if (res.ok) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [serverUrl]);

  // Immediate save for toggles
  const handleToggle = useCallback((field: 'enabled' | 'useDefaultList') => {
    if (!initialLoadComplete.current) return;
    const newConfig = { ...config, [field]: !config[field] };
    setConfig(newConfig);
    void saveSettings(newConfig);
  }, [config, saveSettings]);

  const handleAddWord = useCallback(() => {
    if (!newWord.trim() || !initialLoadComplete.current) return;
    if (config.customWords.includes(newWord.trim())) {
      setNewWord('');
      return;
    }
    const newConfig = { ...config, customWords: [...config.customWords, newWord.trim()] };
    setConfig(newConfig);
    setNewWord('');
    void saveSettings(newConfig);
  }, [config, newWord, saveSettings]);

  const handleRemoveWord = useCallback((word: string) => {
    if (!initialLoadComplete.current) return;
    const newConfig = { ...config, customWords: config.customWords.filter(w => w !== word) };
    setConfig(newConfig);
    void saveSettings(newConfig);
  }, [config, saveSettings]);

  const handleAddWhitelistedWord = useCallback(() => {
    if (!newWhitelistedWord.trim() || !initialLoadComplete.current) return;
    if (config.whitelistedWords.includes(newWhitelistedWord.trim())) {
      setNewWhitelistedWord('');
      return;
    }
    const newConfig = { ...config, whitelistedWords: [...config.whitelistedWords, newWhitelistedWord.trim()] };
    setConfig(newConfig);
    setNewWhitelistedWord('');
    void saveSettings(newConfig);
  }, [config, newWhitelistedWord, saveSettings]);

  const handleRemoveWhitelistedWord = useCallback((word: string) => {
    if (!initialLoadComplete.current) return;
    const newConfig = { ...config, whitelistedWords: config.whitelistedWords.filter(w => w !== word) };
    setConfig(newConfig);
    void saveSettings(newConfig);
  }, [config, saveSettings]);

  return (
    <div className="space-y-4">
      {/* Save Status */}
      {saveStatus !== 'idle' && (
        <div className={`text-xs font-medium ${
          saveStatus === 'saving' ? 'text-gray-400' :
          saveStatus === 'saved' ? 'text-green-400' : 'text-red-400'
        }`}>
          {saveStatus === 'saving' ? 'Saving...' :
           saveStatus === 'saved' ? 'Saved' : 'Error'}
        </div>
      )}

      {/* Enable Toggle */}
      <div className="flex items-center justify-between p-4 bg-black/20 rounded-xl border border-white/10">
        <div>
          <span className="text-white font-medium">Enable Profanity Filter</span>
          <p className="text-gray-500 text-sm">Skip messages containing bad words</p>
        </div>
        <button
          onClick={() => handleToggle('enabled')}
          className={`w-14 h-8 rounded-full transition-colors relative ${
            config.enabled ? 'bg-green-500' : 'bg-white/20'
          }`}
        >
          <div
            className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${
              config.enabled ? 'translate-x-7' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {config.enabled && (
        <>
          {/* Use Default List Toggle */}
          <div className="flex items-center justify-between p-4 bg-black/20 rounded-xl border border-white/10">
            <div>
              <span className="text-white font-medium">Use Default Bad Words List</span>
              <p className="text-gray-500 text-sm">Comprehensive list of common profanity</p>
            </div>
            <button
              onClick={() => handleToggle('useDefaultList')}
              className={`w-14 h-8 rounded-full transition-colors relative ${
                config.useDefaultList ? 'bg-green-500' : 'bg-white/20'
              }`}
            >
              <div
                className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${
                  config.useDefaultList ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Custom Words */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-400">
                Custom Blocked Words
              </label>
              <button
                onClick={() => setShowPatternHelp(true)}
                className="w-4 h-4 rounded-full bg-gray-600 text-gray-300 text-xs flex items-center justify-center hover:bg-gray-500 transition-colors"
                title="Click for more info"
              >
                ?
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddWord()}
                className="flex-1 p-3 bg-black/40 border border-white/10 rounded-xl text-white focus:border-red-500/50 focus:outline-none transition-colors"
                placeholder="Add word or pattern..."
              />
              <button
                onClick={handleAddWord}
                className="px-4 py-3 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors font-medium"
              >
                Add
              </button>
            </div>
            {config.customWords.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {config.customWords.map((word) => (
                  <span
                    key={word}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-300 rounded-lg text-sm"
                  >
                    {word}
                    <button
                      onClick={() => handleRemoveWord(word)}
                      className="ml-1 text-red-400 hover:text-red-200"
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Whitelisted Words */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-gray-400">
              Whitelisted Words
            </label>
            <p className="text-xs text-gray-600">
              Allow these words even if they're in the default list
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={newWhitelistedWord}
                onChange={(e) => setNewWhitelistedWord(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddWhitelistedWord()}
                className="flex-1 p-3 bg-black/40 border border-white/10 rounded-xl text-white focus:border-green-500/50 focus:outline-none transition-colors"
                placeholder="Add whitelisted word..."
              />
              <button
                onClick={handleAddWhitelistedWord}
                className="px-4 py-3 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors font-medium"
              >
                Add
              </button>
            </div>
            {config.whitelistedWords.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {config.whitelistedWords.map((word) => (
                  <span
                    key={word}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-300 rounded-lg text-sm"
                  >
                    {word}
                    <button
                      onClick={() => handleRemoveWhitelistedWord(word)}
                      className="ml-1 text-green-400 hover:text-green-200"
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <p className="text-blue-400 text-sm">
              Messages containing blocked words will be silently skipped - the AI won't read them aloud.
              Changes take effect on session restart.
            </p>
          </div>
        </>
      )}

      {!config.enabled && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <p className="text-amber-400 text-sm">
            When disabled, all messages will be read aloud including those with profanity.
          </p>
        </div>
      )}

      {/* Pattern Help Modal */}
      {showPatternHelp && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowPatternHelp(false)}>
          <div className="bg-gradient-to-br from-gray-900/95 via-purple-900/20 to-gray-900/95 border border-purple-500/30 rounded-2xl p-6 max-w-lg mx-4 shadow-2xl backdrop-blur-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg">Pattern Types</h3>
              <button
                onClick={() => setShowPatternHelp(false)}
                className="text-gray-400 hover:text-white text-xl transition-colors"
              >
                ×
              </button>
            </div>
            <div className="space-y-4 text-sm text-gray-300">
              <div className="p-3 bg-black/30 rounded-xl border border-white/10">
                <p className="text-white font-medium mb-1">Plain Text</p>
                <p>Just type the word or phrase to block.</p>
                <p className="mt-2 text-gray-400">
                  Examples: <code className="bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 rounded text-purple-300">spam</code> <code className="bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 rounded text-purple-300">buy followers</code>
                </p>
              </div>
              
              <div className="p-3 bg-black/30 rounded-xl border border-white/10">
                <p className="text-white font-medium mb-1">Regex Patterns</p>
                <p>Use <code className="bg-purple-500/20 border border-purple-500/30 px-1.5 py-0.5 rounded text-purple-300">regex:</code> prefix for regular expressions.</p>
                <p className="mt-2 text-gray-400">
                  Example: <code className="bg-purple-500/20 border border-purple-500/30 px-2 py-0.5 rounded text-purple-300">regex:free.*money</code> blocks "free money", "free easy money", etc.
                </p>
              </div>
              
              <div className="p-3 bg-black/30 rounded-xl border border-white/10">
                <p className="text-white font-medium mb-1">Wildcard Patterns</p>
                <p>Use <code className="bg-purple-500/20 border border-purple-500/30 px-1.5 py-0.5 rounded text-purple-300">wc:</code> prefix with special characters:</p>
                <ul className="mt-2 space-y-1.5 text-gray-400">
                  <li className="flex items-start gap-2">
                    <code className="bg-yellow-500/20 border border-yellow-500/30 px-1.5 py-0.5 rounded text-yellow-300 shrink-0">_</code>
                    <span>Any single character — <code className="text-purple-300">wc:sh_t</code> matches "sh*t", "shot", etc.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <code className="bg-yellow-500/20 border border-yellow-500/30 px-1.5 py-0.5 rounded text-yellow-300 shrink-0">*</code>
                    <span>Any characters — <code className="text-purple-300">wc:scam*</code> matches "scam", "scammer", "scamming"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <code className="bg-yellow-500/20 border border-yellow-500/30 px-1.5 py-0.5 rounded text-yellow-300 shrink-0">[]</code>
                    <span>One of the chars — <code className="text-purple-300">wc:a[ss]</code> matches "as" or "ass"</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
